import { Button, Row, Tooltip } from "@narmi/design_system";
import { useContext } from "react";
import type { MouseEventHandler } from "react";
import BackCancelButton from "../BackCancelButton";
import ContextForm from "../ContextForm";
import type { ProgressButtonsProps } from "../ProgressButtons";
import "./index.scss";

type StickyProgressButtonsProps = Omit<ProgressButtonsProps, "enableStickyMobile">;

/**
 * Sticky footer progress buttons for mobile views.
 * Fixed to bottom of viewport with NDS Button + BackCancelButton.
 */
const StickyProgressButtons = ({
  backLabel,
  nextLabel,
  onBack,
  onNext,
  hidePrev,
  disableNext = false,
  disabled = false,
  isNextLoading = false,
  dangerouslyDisableShowLoading,
  shouldHideInlineToasts,
  nextTestId = "next",
  backTooltip,
  nextTooltip,
  kind = "primary",
}: StickyProgressButtonsProps) => {
  const formContext = useContext(ContextForm.FormContext);

  const getNextButton = () => {
    const next = (
      <Button
        kind={kind}
        size="s"
        endIcon="chevron-right"
        // Match ProgressButtons' existing JSX-label and context-dependent callback contracts.
        label={nextLabel as string}
        onClick={formContext ? () => {} : (onNext as MouseEventHandler<HTMLButtonElement>)}
        disabled={disableNext}
        isLoading={isNextLoading}
        className="sticky-progress-buttons-next"
        testId={nextTestId}
      />
    );
    return nextTooltip ? <Tooltip text={nextTooltip}>{next}</Tooltip> : next;
  };

  const getBackButton = () => {
    if (hidePrev) return null;
    const back = (
      <BackCancelButton
        onBack={onBack as MouseEventHandler<HTMLButtonElement>}
        backLabel={backLabel}
        disabled={disabled}
      />
    );
    return backTooltip ? <Tooltip text={backTooltip}>{back}</Tooltip> : back;
  };

  return (
    <div className="sticky-progress-buttons-footer">
      <div className="sticky-progress-buttons-container">
        <Row justifyContent="space-between" alignItems="center">
          <Row.Item>{getBackButton()}</Row.Item>
          <Row.Item shrink>
            {formContext ? (
              <ContextForm.Action
                onSubmit={onNext}
                dangerouslyDisableShowLoading={dangerouslyDisableShowLoading}
                shouldHideInlineToasts={shouldHideInlineToasts}
              >
                {getNextButton()}
              </ContextForm.Action>
            ) : (
              getNextButton()
            )}
          </Row.Item>
        </Row>
      </div>
    </div>
  );
};

export default StickyProgressButtons;
